import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    CardMedia,
    Typography,
    IconButton,
    Chip,
    Grid,
    Skeleton,
    Alert,
    Tooltip,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditNoteIcon from '@mui/icons-material/EditNote';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SchoolIcon from '@mui/icons-material/School';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import { useNavigate } from 'react-router-dom';
import { bookmarkService, Bookmark } from '../../services/bookmarkService';
import { useTranslation } from '../../i18n';

const BookmarkedCourses: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [bookmarks, setBookmarks] = useState<Bookmark[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [editingBookmark, setEditingBookmark] = useState<Bookmark | null>(null);
    const [editNote, setEditNote] = useState('');

    useEffect(() => {
        loadBookmarks();
    }, []);

    const loadBookmarks = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await bookmarkService.getBookmarks();
            setBookmarks(data);
        } catch (err) {
            console.error('Error loading bookmarks:', err);
            setError(t('bookmarks.errorLoading'));
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (bookmarkId: number, e: React.MouseEvent) => {
        e.stopPropagation();
        try {
            await bookmarkService.deleteBookmark(bookmarkId);
            setBookmarks(prev => prev.filter(b => b.id !== bookmarkId));
        } catch (err) {
            console.error('Error deleting bookmark:', err);
        }
    };

    const handleEditClick = (bookmark: Bookmark, e: React.MouseEvent) => {
        e.stopPropagation();
        setEditingBookmark(bookmark);
        setEditNote(bookmark.note || '');
    };

    const handleSaveNote = async () => {
        if (!editingBookmark) return;

        try {
            const updated = await bookmarkService.updateNote(editingBookmark.id, editNote);
            setBookmarks(prev => prev.map(b => b.id === updated.id ? updated : b));
            setEditingBookmark(null);
        } catch (err) {
            console.error('Error updating note:', err);
        }
    };

    const handleCourseClick = (courseId: number) => {
        navigate(`/courses/${courseId}`);
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const getDifficultyColor = (difficulty: string): 'success' | 'warning' | 'error' | 'default' => {
        switch (difficulty?.toLowerCase()) {
            case 'beginner':
                return 'success';
            case 'intermediate':
                return 'warning';
            case 'advanced':
                return 'error';
            default:
                return 'default';
        }
    };

    if (loading) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BookmarkIcon color="primary" />
                    {t('bookmarks.title')}
                </Typography>
                <Grid container spacing={3}>
                    {[1, 2, 3].map((i) => (
                        <Grid size={{ xs: 12, sm: 6, md: 4 }} key={i}>
                            <Card>
                                <Skeleton variant="rectangular" height={140} />
                                <CardContent>
                                    <Skeleton variant="text" height={32} />
                                    <Skeleton variant="text" width="60%" />
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <BookmarkIcon color="primary" />
                {t('bookmarks.title')}
                <Chip label={bookmarks.length} size="small" color="primary" sx={{ ml: 1 }} />
            </Typography>

            {bookmarks.length === 0 ? (
                <Alert severity="info" sx={{ mt: 2 }}>
                    {t('bookmarks.noBookmarks')}
                </Alert>
            ) : (
                <Grid container spacing={3} sx={{ mt: 1 }}>
                    {bookmarks.map((bookmark) => (
                        <Grid size={{ xs: 12, sm: 6, md: 4 }} key={bookmark.id}>
                            <Card
                                sx={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    cursor: 'pointer',
                                    transition: 'transform 0.2s, box-shadow 0.2s',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: 4
                                    }
                                }}
                                onClick={() => handleCourseClick(bookmark.course.id)}
                            >
                                <CardMedia
                                    component="img"
                                    height="140"
                                    image={bookmark.course.thumbnailUrl || '/placeholder-course.jpg'}
                                    alt={bookmark.course.title}
                                    sx={{ objectFit: 'cover' }}
                                />
                                <CardContent sx={{ flexGrow: 1 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                        <Typography variant="h6" component="h3" sx={{ flexGrow: 1, pr: 1 }}>
                                            {bookmark.course.title}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                            <Tooltip title={t('bookmarks.editNote')}>
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => handleEditClick(bookmark, e)}
                                                >
                                                    <EditNoteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={t('bookmarks.remove')}>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={(e) => handleDelete(bookmark.id, e)}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Box>

                                    <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                                        <Chip
                                            icon={<SchoolIcon />}
                                            label={bookmark.course.difficulty}
                                            size="small"
                                            color={getDifficultyColor(bookmark.course.difficulty)}
                                        />
                                        {bookmark.course.estimatedDuration && (
                                            <Chip
                                                icon={<AccessTimeIcon />}
                                                label={`${bookmark.course.estimatedDuration} min`}
                                                size="small"
                                                variant="outlined"
                                            />
                                        )}
                                    </Box>

                                    {bookmark.note && (
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{
                                                mt: 1,
                                                p: 1,
                                                bgcolor: 'action.hover',
                                                borderRadius: 1,
                                                fontStyle: 'italic'
                                            }}
                                        >
                                            "{bookmark.note}"
                                        </Typography>
                                    )}

                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                        {t('bookmarks.savedOn')} {formatDate(bookmark.createdAt)}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* Edit Note Dialog */}
            <Dialog open={!!editingBookmark} onClose={() => setEditingBookmark(null)} maxWidth="sm" fullWidth>
                <DialogTitle>{t('bookmarks.editNote')}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label={t('bookmarks.noteLabel')}
                        fullWidth
                        multiline
                        rows={3}
                        value={editNote}
                        onChange={(e) => setEditNote(e.target.value)}
                        placeholder={t('bookmarks.notePlaceholder')}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditingBookmark(null)}>{t('common.cancel')}</Button>
                    <Button onClick={handleSaveNote} variant="contained">{t('common.save')}</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default BookmarkedCourses;
